import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Building,
  Users,
  Phone,
  MapPin,
} from '@phosphor-icons/react';
import Bed from '../../components/icons/RoomBedIcon';
import api from '../../services/api';
import { getVenueLocationUrl } from '../../lib/locationLinks';
import InfoTip from '../../components/InfoTip';

export default function MyRoom() {
  const { currentEvent } = useAuth();
  const [room, setRoom] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [roommates, setRoommates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchData = useCallback(async () => {
    if (!currentEvent?.id) return;
    setLoading(true);
    setLoadError('');
    try {
      const response = await api.get(`/events/${currentEvent.id}/my-accommodation`);
      setRoom(response.data?.room || null);
      setHotel(response.data?.hotel || null);
      setRoommates(response.data?.roommates || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setRoom(null);
      setHotel(null);
      setRoommates([]);
      setLoadError('We could not load your accommodation details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hotelLocation = hotel
    ? { ...hotel, name: hotel.hotel_name || room?.hotel_name }
    : null;
  const hotelLocationUrl = getVenueLocationUrl(hotelLocation);
  const hotelAddress = hotel?.address || hotel?.venue_address;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-wedding-sage/20 rounded w-1/4"></div>
          <div className="h-64 bg-wedding-sage/10 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-1">
          <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="room-title">My Room</h1>
          <InfoTip content="View your accommodation details." label="About my room" />
        </div>

        {loadError ? (
          <Card>
            <CardContent className="py-12 text-center">
              <h3 className="font-serif text-xl font-semibold mb-2">Accommodation unavailable</h3>
              <p className="text-muted-foreground">{loadError}</p>
              <button
                type="button"
                onClick={fetchData}
                className="mt-4 rounded-md bg-wedding-sage px-4 py-2 text-sm font-semibold text-white hover:bg-wedding-sage/90"
              >
                Try again
              </button>
            </CardContent>
          </Card>
        ) : room ? (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Room Details */}
            <Card className="md:col-span-2">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="w-20 h-20 bg-wedding-sage rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bed className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="font-serif text-2xl font-bold text-wedding-charcoal">
                          Room {room.room_number}
                        </h2>
                        <p className="text-lg text-muted-foreground">{room.hotel_name}</p>
                        {hotelAddress && (
                          <p className="mt-1 max-w-xl text-sm text-muted-foreground">{hotelAddress}</p>
                        )}
                        {hotelLocationUrl && (
                          <a
                            href={hotelLocationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-wedding-sage hover:underline"
                          >
                            <MapPin className="h-4 w-4" />
                            View hotel location
                          </a>
                        )}
                      </div>
                      <Badge className="bg-wedding-sage text-white">Assigned</Badge>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4 mt-6">
                      <div className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-wedding-sage" />
                        <div>
                          <p className="text-xs text-muted-foreground">Floor</p>
                          <p className="font-medium">{room.floor || 'N/A'}</p>
                        </div>
                      </div>
                      {room.wing && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-wedding-sage" />
                          <div>
                            <p className="text-xs text-muted-foreground">Wing/Block</p>
                            <p className="font-medium">{room.wing}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-wedding-sage" />
                        <div>
                          <p className="text-xs text-muted-foreground">Capacity</p>
                          <p className="font-medium">{room.occupancy || 0}/{room.capacity} guests</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Roommates */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-wedding-sage" />
                  Roommates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {roommates.length > 0 ? (
                  <div className="space-y-3">
                    {roommates.map((mate) => (
                      <div key={mate.id} className="flex items-center justify-between p-3 rounded-lg bg-wedding-sage/5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-wedding-sage/20 flex items-center justify-center">
                            <span className="font-semibold text-wedding-sage">
                              {mate.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{mate.name}</p>
                            {mate.phone && (
                              <p className="text-sm text-muted-foreground">{mate.phone}</p>
                            )}
                          </div>
                        </div>
                        {mate.phone && (
                          <a href={`tel:${mate.phone}`} className="text-wedding-sage hover:text-wedding-sage/80">
                            <Phone className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No roommates assigned yet.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Room Notes */}
            {room.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{room.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Bed className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-serif text-xl font-semibold mb-2">No Room Assigned</h3>
              <p className="text-muted-foreground">
                You haven't been assigned a room yet. Please contact the event organizer for accommodation details.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
